import { HtsButton } from '@hts/shared/ui';
import { Component, PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';
import styles from '../styles/ErrorBoundary.module.scss';

interface ErrorBoundaryState {
  hasError: boolean;
  errorInfo?: any;
  error?: Error;
}

export class ErrorBoundary extends Component<PropsWithChildren> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  componentDidCatch(error: Error, errorInfo: any) {
    Sentry.captureException(error, {
      extra: {
        errorInfo,
      },
    });

    this.setState({
      hasError: true,
      errorInfo,
      error,
    });
  }

  componentDidUpdate(prevProps: PropsWithChildren) {
    if (this.state.hasError && prevProps !== this.props) {
      this.setState({ hasError: false, errorInfo: undefined, error: undefined });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, errorInfo: undefined, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className={styles.container}>
          <div className={styles.centerContainer}>
            <h2>Something goes wrong.</h2>
            <p>Please try again or reload the page.</p>

            <HtsButton
              variant="contained"
              label="Try Again"
              className={styles.resetButton}
              onClick={this.handleReset}
            />

            {isDevelopment && this.state.error && (
              <details className={styles.errorDetails}>
                <summary>Error Details (Development)</summary>

                <pre className={styles.errorStack}>
                  {this.state.error.toString()}

                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
